import { ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  NavigationType,
  useLocation,
  useNavigate,
  useNavigationType,
  useParams,
} from "react-router-dom";

import { ROUTES } from "@/constants/routes";
import { TENANT_ID_STORAGE_KEY } from "@/lib/contexts/Auth/AuthContext";
import { useAuth } from "@/lib/hooks/useAuth";
import { useClients } from "@/lib/hooks/useClients";
import { useTenants } from "@/lib/hooks/useTenants";
import useToast from "@/lib/hooks/useToast";
import {
  getOverviewPageForPath,
  isNestedClientPage,
} from "@/lib/utils/clientRouteUtils";
import { UserRole } from "@/types/user/userType";

const INITIAL_LOCATION_KEY = "default";

interface ClientTenantGuardProps {
  children: ReactNode;
}

const createPhase = (
  clientId: string | undefined,
  tenantId: string | null,
  navigationType: NavigationType,
  locationKey: string,
) => ({
  clientId,
  hasRun: false,
  baselineTenantId: tenantId,
  leavesOnMismatch:
    navigationType === NavigationType.Pop &&
    locationKey !== INITIAL_LOCATION_KEY,
});

export const ClientTenantGuard = ({ children }: ClientTenantGuardProps) => {
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { pathname, key: locationKey } = useLocation();
  const { clientId } = useParams();
  const [prevClientId, setPrevClientId] = useState<string | null>(null);

  const { t } = useTranslation();
  const { role, tenantId, setTenantId } = useAuth();
  const [phase, setPhase] = useState(() =>
    createPhase(clientId, tenantId, navigationType, locationKey),
  );
  if (phase.clientId !== clientId) {
    setPhase(createPhase(clientId, tenantId, navigationType, locationKey));
  }
  const { client, clientLoading, clientFetching } = useClients();
  const { tenants, tenantsLoading, tenantsFetching } = useTenants();
  const toast = useToast();

  const isReady =
    !clientFetching && !tenantsFetching && !clientLoading && !tenantsLoading;
  const markRun = useCallback(
    () => setPhase((current) => ({ ...current, hasRun: true })),
    [],
  );
  const leaveToClients = useCallback(() => {
    navigate(`/${ROUTES.clients}`, { replace: true });
    markRun();
  }, [navigate, markRun]);
  const needsTenantSwitch = useCallback(() => {
    if (
      client &&
      client.tenantId != null &&
      tenantId != null &&
      client.tenantId !== tenantId
    ) {
      if (phase.leavesOnMismatch) {
        leaveToClients();
        return;
      }

      const tenantName = tenants?.data?.find(
        (tenant) => tenant.id === client.tenantId,
      )?.name;

      if (!tenantName) {
        toast.showToast(
          t("clientTenantGuard.notAuthorized"),
          "error",
        );
        leaveToClients();
        return;
      }

      setTenantId(client.tenantId);
      toast.showToast(t("clientTenantGuard.switchedToTenant", { tenantName }), "success");
      markRun();
    }
  }, [
    client,
    tenantId,
    tenants,
    toast,
    setTenantId,
    t,
    phase.leavesOnMismatch,
    leaveToClients,
    markRun,
  ]);

  // Redirect to overview when clientId in URL changed and we're on a nested page
  // (e.g. pasted URL or manual edit). Entity IDs are client-specific.
  useEffect(() => {
    if (!isReady || !clientId) return;

    const clientChanged =
      prevClientId !== null && clientId !== prevClientId;
    if (clientChanged && isNestedClientPage(pathname, clientId)) {
      const overviewPage = getOverviewPageForPath(pathname, clientId);
      navigate(`/${ROUTES.clients}/${clientId}/${overviewPage}`, {
        replace: true,
      });
      return;
    }

    setPrevClientId(clientId);
  }, [isReady, clientId, pathname, navigate, prevClientId]);

  useEffect(() => {
    if (!isReady) return;
    if (role !== UserRole.SUPER_ADMIN) return;
    if (phase.baselineTenantId !== tenantId) return;
    if (phase.hasRun) return;

    needsTenantSwitch();
  }, [isReady, role, needsTenantSwitch, phase, tenantId]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== TENANT_ID_STORAGE_KEY) return;
      if (!event.newValue) return;
      if (role !== UserRole.SUPER_ADMIN) return;
      const clientStays =
        client &&
        (client.tenantId == null || client.tenantId === event.newValue);
      if (clientStays) return;

      navigate(`/${ROUTES.clients}`, { replace: true });
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [role, client, navigate]);

  return <>{children}</>;
};
